import { matchPath } from "react-router-dom";
import type { BreadcrumbItem } from "Helpers/generalBreadcrumbs";

type BreadcrumbRule = {
  path: string;
  crumbs: (params: Record<string, string | undefined>) => BreadcrumbItem[];
};

const stations = (): BreadcrumbItem => ({
  labelKey: "stations",
});

export const buildStationDetailsHref = (
  p: Record<string, string | undefined>,
) => (p.id ? `/station/${p.id}` : undefined);

export const buildStoreDetailsHref = (
  p: Record<string, string | undefined>,
) => (p.id ? `/store/${p.id}` : undefined);

/** Stations sidebar module routes — more specific paths first. */
const STATIONS_BREADCRUMB_RULES: BreadcrumbRule[] = [
  // Stations list
  {
    path: "/stations",
    crumbs: () => [stations(), { labelKey: "stationsList" }],
  },
  {
    path: "/station/create",
    crumbs: () => [
      stations(),
      { labelKey: "stationsList", href: "/stations" },
      { labelKey: "create" },
    ],
  },
  {
    path: "/station/assign/store/:id",
    crumbs: (p) => [
      stations(),
      { labelKey: "stationsList", href: "/stations" },
      {
        labelKey: "Details",
        href: buildStationDetailsHref(p),
      },
      { labelKey: "assignStore" },
    ],
  },
  {
    path: "/station/assign/account/:id",
    crumbs: (p) => [
      stations(),
      { labelKey: "stationsList", href: "/stations" },
      {
        labelKey: "Details",
        href: buildStationDetailsHref(p),
      },
      { labelKey: "assignAccount" },
    ],
  },
  {
    path: "/station/assign/user/:id",
    crumbs: (p) => [
      stations(),
      { labelKey: "stationsList", href: "/stations" },
      {
        labelKey: "Details",
        href: buildStationDetailsHref(p),
      },
      { labelKey: "assignUser" },
    ],
  },
  {
    path: "/station/assign/bay/:id",
    crumbs: (p) => [
      stations(),
      { labelKey: "stationsList", href: "/stations" },
      {
        labelKey: "Details",
        href: buildStationDetailsHref(p),
      },
      { labelKey: "assignBay" },
    ],
  },
  {
    path: "/station/assign/warehouse/:id",
    crumbs: (p) => [
      stations(),
      { labelKey: "stationsList", href: "/stations" },
      {
        labelKey: "Details",
        href: buildStationDetailsHref(p),
      },
      { labelKey: "assignWarehouse" },
    ],
  },
  {
    path: "/station/:id/terms-and-conditions",
    crumbs: (p) => [
      stations(),
      { labelKey: "stationsList", href: "/stations" },
      {
        labelKey: "Details",
        href: p.id ? `/station/${p.id}` : undefined,
      },
      { labelKey: "termsAndConditions" },
    ],
  },
  {
    path: "/station/:id/plan-usage",
    crumbs: (p) => [
      stations(),
      { labelKey: "stationsList", href: "/stations" },
      {
        labelKey: "Details",
        href: p.id ? `/station/${p.id}` : undefined,
      },
      { labelKey: "planUsage" },
    ],
  },
  {
    path: "/station/:id",
    crumbs: () => [
      stations(),
      { labelKey: "stationsList", href: "/stations" },
      { labelKey: "Details" },
    ],
  },

  // Station groups
  {
    path: "/station-groups",
    crumbs: () => [stations(), { labelKey: "stationsGroup" }],
  },
  {
    path: "/station-group/create",
    crumbs: () => [
      stations(),
      { labelKey: "stationsGroup", href: "/station-groups" },
      { labelKey: "create" },
    ],
  },
  {
    path: "/station-group/assign/station/:id",
    crumbs: (p) => [
      stations(),
      { labelKey: "stationsGroup", href: "/station-groups" },
      {
        labelKey: "Details",
        href: p.id ? `/station-group/${p.id}` : undefined,
      },
      { labelKey: "assignStation" },
    ],
  },
  {
    path: "/station-group/:id",
    crumbs: () => [
      stations(),
      { labelKey: "stationsGroup", href: "/station-groups" },
      { labelKey: "Details" },
    ],
  },

  // Stores
  {
    path: "/stores",
    crumbs: () => [stations(), { labelKey: "stores" }],
  },
  {
    path: "/store/create",
    crumbs: () => [
      stations(),
      { labelKey: "stores", href: "/stores" },
      { labelKey: "create" },
    ],
  },
  {
    path: "/store/assign/part/:id",
    crumbs: (p) => [
      stations(),
      { labelKey: "stores", href: "/stores" },
      {
        labelKey: "Details",
        href: buildStoreDetailsHref(p),
      },
      { labelKey: "assignPart" },
    ],
  },
  {
    path: "/store/:id/rack/create",
    crumbs: (p) => [
      stations(),
      { labelKey: "stores", href: "/stores" },
      {
        labelKey: "Details",
        href: p.id ? `/store/${p.id}` : undefined,
      },
      { labelKey: "racks", href: p.id ? `/store/${p.id}/racks` : undefined },
      { labelKey: "create" },
    ],
  },
  {
    path: "/store/:id/rack/:rackId",
    crumbs: (p) => [
      stations(),
      { labelKey: "stores", href: "/stores" },
      {
        labelKey: "Details",
        href: p.id ? `/store/${p.id}` : undefined,
      },
      { labelKey: "racks", href: p.id ? `/store/${p.id}/racks` : undefined },
      { labelKey: "editRack" },
    ],
  },
  {
    path: "/store/:id/racks",
    crumbs: (p) => [
      stations(),
      { labelKey: "stores", href: "/stores" },
      {
        labelKey: "Details",
        href: p.id ? `/store/${p.id}` : undefined,
      },
      { labelKey: "racks" },
    ],
  },
  {
    path: "/store/:id",
    crumbs: () => [
      stations(),
      { labelKey: "stores", href: "/stores" },
      { labelKey: "Details" },
    ],
  },

  // Bays
  {
    path: "/bays",
    crumbs: () => [stations(), { labelKey: "bays" }],
  },
  {
    path: "/bay/create",
    crumbs: () => [
      stations(),
      { labelKey: "bays", href: "/bays" },
      { labelKey: "create" },
    ],
  },
  {
    path: "/bay/:id",
    crumbs: () => [
      stations(),
      { labelKey: "bays", href: "/bays" },
      { labelKey: "Details" },
    ],
  },
];

export const getStationsBreadcrumbs = (
  pathname: string,
): BreadcrumbItem[] | null => {
  for (const rule of STATIONS_BREADCRUMB_RULES) {
    const match = matchPath({ path: rule.path, end: true }, pathname);
    if (match) {
      return rule.crumbs(match.params as Record<string, string | undefined>);
    }
  }
  return null;
};
